/** Primary + subcategory taxonomy (Do or Dump funnel). */

export type ProductPrimaryId =
  | "fashion"
  | "hair"
  | "beauty"
  | "jewelry"
  | "accessories"
  | "home"
  | "digital"
  | "art"
  | "food"
  | "novelty"
  | "other";

/** Funnel UI: no primary chosen yet (`""`). */
export type ProductPrimarySelection = ProductPrimaryId | "";

export const PRODUCT_PRIMARIES: { id: ProductPrimaryId; label: string }[] = [
  { id: "fashion", label: "Fashion" },
  { id: "hair", label: "Hair" },
  { id: "beauty", label: "Beauty" },
  { id: "jewelry", label: "Jewelry" },
  { id: "accessories", label: "Accessories" },
  { id: "home", label: "Home" },
  { id: "digital", label: "Digital" },
  { id: "art", label: "Art" },
  { id: "food", label: "Food" },
  { id: "novelty", label: "Novelty / viral" },
  { id: "other", label: "Other" },
];

export const PRODUCT_SUBCATEGORIES: Record<ProductPrimaryId, { id: string; label: string }[]> = {
  fashion: [
    { id: "streetwear", label: "Streetwear" },
    { id: "luxury-inspired", label: "Luxury-inspired" },
    { id: "basics-essentials", label: "Basics / essentials" },
    { id: "womens-fashion", label: "Women's fashion" },
    { id: "mens-fashion", label: "Men's fashion" },
    { id: "activewear", label: "Activewear" },
    { id: "lounge-cozy", label: "Lounge / cozy" },
    { id: "cut-sew-custom", label: "Cut & sew / custom" },
    { id: "vintage-thrifted", label: "Vintage / thrifted" },
    { id: "upcycled-reworked", label: "Upcycled / reworked" },
    { id: "fashion-accessories", label: "Accessories (belts, hats, etc.)" },
  ],
  hair: [
    { id: "wigs", label: "Wigs" },
    { id: "bundles", label: "Bundles" },
    { id: "closures-frontals", label: "Closures / frontals" },
    { id: "clip-ins", label: "Clip-ins" },
    { id: "extensions-general", label: "Extensions (general)" },
    { id: "hair-tools", label: "Hair tools (flat irons, etc.)" },
    { id: "hair-care-products", label: "Hair care products" },
    { id: "edge-styling", label: "Edge control / styling products" },
  ],
  beauty: [
    { id: "makeup-general", label: "Makeup (general)" },
    { id: "lip-products", label: "Lip products" },
    { id: "lashes", label: "Lashes" },
    { id: "skincare", label: "Skincare" },
    { id: "body-care", label: "Body care" },
    { id: "fragrance", label: "Fragrance" },
    { id: "nail-products", label: "Nail products" },
  ],
  jewelry: [
    { id: "gold-jewelry", label: "Gold jewelry" },
    { id: "silver-jewelry", label: "Silver jewelry" },
    { id: "custom-jewelry", label: "Custom jewelry" },
    { id: "nameplate-personalized", label: "Nameplate / personalized" },
    { id: "statement-pieces", label: "Statement pieces" },
    { id: "minimal-dainty", label: "Minimal / dainty" },
  ],
  accessories: [
    { id: "bags-purses", label: "Bags / purses" },
    { id: "phone-accessories", label: "Phone accessories" },
    { id: "tech-accessories", label: "Tech accessories" },
    { id: "sunglasses", label: "Sunglasses" },
    { id: "watches", label: "Watches" },
    { id: "small-leather-goods", label: "Small leather goods" },
  ],
  home: [
    { id: "candles", label: "Candles" },
    { id: "incense", label: "Incense" },
    { id: "room-sprays", label: "Room sprays" },
    { id: "home-decor", label: "Home decor" },
    { id: "bedding-textiles", label: "Bedding / textiles" },
  ],
  digital: [
    { id: "ebooks", label: "Ebooks" },
    { id: "guides", label: "Guides" },
    { id: "templates", label: "Templates" },
    { id: "presets", label: "Presets (photo/video)" },
    { id: "courses-lightweight", label: "Courses (lightweight)" },
    { id: "toolkits", label: "Toolkits" },
  ],
  art: [
    { id: "prints", label: "Prints" },
    { id: "paintings", label: "Paintings" },
    { id: "custom-art", label: "Custom art" },
    { id: "posters", label: "Posters" },
    { id: "stickers", label: "Stickers" },
  ],
  food: [
    { id: "snacks", label: "Snacks" },
    { id: "candy", label: "Candy" },
    { id: "baked-goods", label: "Baked goods" },
    { id: "drinks-mixes", label: "Drinks / mixes" },
  ],
  novelty: [
    { id: "trending-items", label: "Trending items" },
    { id: "tiktok-viral", label: "TikTok viral products" },
    { id: "gag-joke", label: "Gag / joke products" },
    { id: "niche-fandom", label: "Niche fandom items" },
  ],
  other: [],
};

export function getSubcategories(primary: ProductPrimaryId) {
  return PRODUCT_SUBCATEGORIES[primary] ?? [];
}

export function getPrimaryLabel(id: ProductPrimaryId): string {
  return PRODUCT_PRIMARIES.find((p) => p.id === id)?.label ?? id;
}

export function getSubLabel(primary: ProductPrimaryId, subId: string): string | null {
  const row = getSubcategories(primary).find((s) => s.id === subId);
  return row?.label ?? null;
}

/** Human-readable path for UI + AI placeholders. */
export function formatProductCategoryPath(
  primary: ProductPrimarySelection,
  subId: string,
  notes?: string
): string {
  const trimmed = (notes ?? "").trim();
  if (!primary) {
    return "Select category";
  }
  if (primary === "other") {
    return trimmed || "Other (describe below)";
  }
  const p = getPrimaryLabel(primary);
  const subs = getSubcategories(primary);
  const s = subId ? getSubLabel(primary, subId) : null;
  const subPart = s ? ` → ${s}` : subs.length > 0 ? " → Select subcategory" : "";
  const notePart = trimmed ? ` · ${trimmed}` : "";
  return `${p}${subPart}${notePart}`;
}

/**
 * Dashboard / narrative copy: prefer plain subcategory name (e.g. “Skincare”), not “Beauty → Skincare”.
 */
export function formatDashboardCategoryLane(primary: string, subId: string): string {
  const known = PRODUCT_PRIMARIES.some((p) => p.id === primary);
  if (!known || !primary) {
    const slug = subId.replace(/-/g, " ").trim();
    return slug ? slug.charAt(0).toUpperCase() + slug.slice(1) : "your category";
  }
  const pid = primary as ProductPrimaryId;
  if (pid === "other") {
    const slug = subId.replace(/-/g, " ").trim();
    return slug ? slug.charAt(0).toUpperCase() + slug.slice(1) : "Other";
  }
  const subLabel = subId ? getSubLabel(pid, subId) : null;
  if (subLabel) return subLabel;
  return getPrimaryLabel(pid);
}
